import { useState } from 'react';
import { IconCamera, IconArrowLeft, IconCheck, IconPhoto, IconSend, IconTrash, IconX } from '@tabler/icons-react';
import { Link } from '@tanstack/react-router';
import { PageHeader } from '@hana/shell';
import { useT } from '@hana/i18n';
import { StateView, useToast, type ScreenState } from '@hana/ui';
import { NAME, NEED, MODES, PTI, SEED_SHOTS, SIMS, SIM_RF, quality, rfText, sendLabel, shoot, type PhotoMode, type Shot, type Sim } from './photosData';
import './i18n';
import s from './photos.module.css';

const STATES: ScreenState[] = ['ready', 'loading', 'empty', 'error', 'noperm'];
const forced = (): ScreenState => {
  const v = new URLSearchParams(location.search).get('state');
  return STATES.includes(v as ScreenState) ? (v as ScreenState) : 'ready';
};
const hm = () => { const d = new Date(); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };

/** Mint-Mov-Phone2 khung "Chụp ảnh": ePOD · EIR · máy lạnh · PTI (giả lập, giữ trong bộ nhớ; chưa nối camera thật). */
export function Photos() {
  const state = forced();
  const toast = useToast();
  const t = useT();
  const [mode, setMode] = useState<PhotoMode>('rf');
  const [sim, setSim] = useState<Sim>('ok');
  const [shots, setShots] = useState<Shot[]>(SEED_SHOTS);
  const [bad, setBad] = useState<string | null>(null);
  const [rf, setRf] = useState<{ v: number; dev: number } | null>(null);
  const [rcv, setRcv] = useState('');
  const [sent, setSent] = useState<Partial<Record<PhotoMode, boolean>>>({});
  const [pti, setPti] = useState<Record<number, 'ok' | 'bad'>>({});
  const [ptiPh, setPtiPh] = useState<Record<number, boolean>>({});
  const [ptiTry, setPtiTry] = useState(false);
  const [ptiSent, setPtiSent] = useState(false);

  const offline = state === 'error';
  const noCam = state === 'noperm';
  const q = quality(sim);
  const cam = mode !== 'pti' ? mode : null;
  const mine = shots.filter((x) => x.mode === mode);
  const canSend = mine.length > 0 && !(mode === 'epod' && rcv.trim().length < 3) && !noCam;
  const sims = mode === 'rf' ? [...SIMS, SIM_RF] : SIMS;

  const pick = (m: PhotoMode) => { setMode(m); setBad(null); setRf(null); setSim('ok'); };
  const take = () => {
    if (noCam || !cam) return;
    const r = shoot(cam, sim, hm(), `s${Date.now()}`);
    if ('bad' in r) { setBad(r.bad); setRf(null); return; }
    setShots((l) => [r.shot, ...l]);
    setBad(null);
    setRf(r.rf);
    toast({ msg: t('mov.ph.accepted'), undo: () => setShots((l) => l.filter((x) => x.id !== r.shot.id)) });
  };
  const send = () => {
    if (!canSend) { toast({ msg: mine.length ? t('mov.ph.needReceiver') : t('mov.ph.noGood') }); return; }
    setSent((o) => ({ ...o, [mode]: true }));
    toast({ msg: offline ? t('mov.ph.savedOffline', { n: mine.length }) : t('mov.ph.sentJob', { n: mine.length, name: cam ? NAME[cam] : '' }) });
  };
  const missing = PTI.filter((_, i) => !pti[i]).length;
  const noPhoto = PTI.filter((_, i) => pti[i] === 'bad' && !ptiPh[i]).length;
  const sendPti = () => {
    if (missing || noPhoto) { setPtiTry(true); return; }
    setPtiSent(true);
    setPtiTry(false);
    toast({ msg: t('mov.ph.ptiToast') });
  };

  return (
    <>
      <PageHeader lead={<Link to="/mov" className={s.back} aria-label={t('mov.ph.back')}><IconArrowLeft size={26} stroke={2.2} aria-hidden="true" /></Link>} title={t('mov.ph.title')} sub={t('mov.ph.sub')} />
      <div className={s.page}>
        <div role="tablist" aria-label={t('mov.ph.kind')} className={s.tabs}>
          {MODES.map((m) => <button key={m.key} type="button" role="tab" aria-selected={mode === m.key} onClick={() => pick(m.key)}>{m.label}</button>)}
        </div>
        {offline && <div role="status" className={s.net}><b>{t('mov.ph.offline')}</b> {t('mov.ph.offlineDesc')}</div>}
        {noCam && <div className={s.empty}><b>{t('mov.ph.noCam')}</b><div>{t('mov.ph.noCamDesc')}</div></div>}
        {state === 'empty' && <div className={s.empty}><b>{t('mov.ph.none')}</b><div>{t('mov.ph.noneDesc')}</div></div>}
        <StateView
          state={state === 'loading' ? 'loading' : 'ready'}
          skeleton={<div aria-hidden="true" style={{ height: 360, borderRadius: 20, background: 'var(--skel)' }} />}
        >
          {state !== 'empty' && !noCam && cam && (
            <>
              <div className={s.need}>{NEED[cam]}</div>
              <div className={s.vf}>
                <div className={s.paper} aria-hidden="true" data-rf={mode === 'rf' ? '1' : '0'} data-sim={sim}><i /><i /></div>
                {sim === 'glare' && <div className={s.glare} aria-hidden="true" />}
                <div className={s.frame} aria-hidden="true" data-bad={q.sharp && q.light && q.edge ? '0' : '1'} />
                <div className={s.wm} aria-label={t('mov.ph.stamp')}>{`TRF2610078 · 51C-482.17\n22/Oct/2026 ${hm()} · GPS 10.8231, 106.7543`}</div>
                <div className={s.chips}>
                  {([[t('mov.ph.sharp'), q.sharp], [t('mov.ph.light'), q.light], [t('mov.ph.edge'), q.edge]] as const).map(([k, ok]) => (
                    <span key={k} data-bad={ok ? '0' : '1'}>{ok ? <IconCheck size={14} stroke={3} aria-hidden="true" /> : <IconX size={14} stroke={3} aria-hidden="true" />}{k}</span>
                  ))}
                </div>
              </div>
              <div>
                <div className={s.lb}>{t('mov.ph.sim')}</div>
                <div className={s.sims}>
                  {sims.map(([k, l]) => <button key={k} type="button" aria-pressed={sim === k} onClick={() => { setSim(k); setBad(null); }}>{l}</button>)}
                </div>
              </div>
              {bad && <div role="alert" className={s.alert}>{bad}</div>}
              {mode === 'rf' && rf && <div role="status" className={s.rf} data-bad={rf.dev > 2 ? '1' : '0'}>{rfText(rf)}</div>}
              {mode === 'epod' && (
                <label className={s.field}>
                  <span>{t('mov.ph.receiver')}</span>
                  <input value={rcv} onChange={(e) => setRcv(e.target.value)} placeholder={t('mov.ph.receiverPh')} />
                </label>
              )}
              <div className={s.shutter}>
                <button type="button" className={s.lib} aria-label={t('mov.ph.library')} onClick={() => toast({ msg: t('mov.ph.libraryToast') })}><IconPhoto size={24} stroke={1.9} aria-hidden="true" /></button>
                <button type="button" className={s.shoot} aria-label={t('mov.ph.shoot')} onClick={take}><IconCamera size={30} stroke={2} aria-hidden="true" /></button>
                <span className={s.count} aria-label={t('mov.ph.taken', { n: mine.length })}>{mine.length}</span>
              </div>
              {mine.length > 0 && (
                <div className={s.shots}>
                  {mine.map((x) => (
                    <div key={x.id} className={s.shot}>
                      <span className={s.thumb} aria-hidden="true" />
                      <div><div>{x.k}</div><div className={s.lb}>{x.d}</div></div>
                      <button type="button" className={s.del} aria-label={t('mov.ph.delete', { k: x.k })} onClick={() => { setShots((l) => l.filter((y) => y.id !== x.id)); toast({ msg: t('mov.ph.deleted') }); }}><IconTrash size={20} stroke={2} aria-hidden="true" /></button>
                    </div>
                  ))}
                </div>
              )}
              <button type="button" className={s.send} aria-disabled={!canSend} onClick={send}><IconSend size={16} stroke={1.8} aria-hidden="true" />{sendLabel(mine.length, !!sent[mode], offline)}</button>
            </>
          )}
          {state !== 'empty' && !noCam && mode === 'pti' && (
            <>
              <div className={s.banner} data-ok={ptiSent ? '1' : '0'}>{ptiSent ? t('mov.ph.ptiSent', { hm: hm() }) : t('mov.ph.ptiNeed')}</div>
              {PTI.map((k, i) => (
                <div key={k} className={s.pti}>
                  <b>{k}</b>
                  <div role="radiogroup" aria-label={k} className={s.pair}>
                    <button type="button" role="radio" data-v="ok" aria-checked={pti[i] === 'ok'} onClick={() => { setPti((o) => ({ ...o, [i]: 'ok' })); setPtiTry(false); }}>{t('mov.ph.ok')}</button>
                    <button type="button" role="radio" data-v="bad" aria-checked={pti[i] === 'bad'} onClick={() => { setPti((o) => ({ ...o, [i]: 'bad' })); setPtiTry(false); }}>{t('mov.ph.bad')}</button>
                  </div>
                  {pti[i] === 'bad' && (
                    <button type="button" className={s.photo} onClick={() => { setPtiPh((o) => ({ ...o, [i]: true })); toast({ msg: t('mov.ph.defectSaved') }); }}>
                      <IconCamera size={16} stroke={1.8} aria-hidden="true" />{ptiPh[i] ? t('mov.ph.defectRetake') : t('mov.ph.defectTake')}
                    </button>
                  )}
                </div>
              ))}
              {ptiTry && (missing > 0 || noPhoto > 0) && <div role="alert" className={s.perr}>{[missing ? t('mov.ph.missing', { n: missing }) : '', noPhoto ? t('mov.ph.noPhoto', { n: noPhoto }) : ''].filter(Boolean).join(' · ')}</div>}
              <button type="button" className={s.send} onClick={sendPti}><IconSend size={16} stroke={1.8} aria-hidden="true" />{t('mov.ph.sendPti')}</button>
            </>
          )}
        </StateView>
      </div>
    </>
  );
}
